/* V158 — Admin Agents Tab.
   Single source of truth for all 34 AI agents (14 live + 20 coming-soon).
   Joins catalog + executions (30d) + demand votes + active workspaces.

   KPI tiles + filter chips (All/Live/Coming-soon) + sortable table.
*/
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "../../config";

const SORT_OPTIONS = [
  { key: "runs_30d", label: "Beh. 30d" },
  { key: "active_workspaces", label: "Aktívne klienti" },
  { key: "demand_votes", label: "Demand votes" },
  { key: "thumbs_up_pct", label: "👍 %" },
  { key: "fallback_pct", label: "Fallback %" },
  { key: "price_eur_month", label: "Cena" },
];

export default function AdminAgentsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all"); // all | live | soon
  const [sortKey, setSortKey] = useState("runs_30d");
  const [days, setDays] = useState(30);

  const fetchData = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/agents/overview?days=${days}`);
      setData(r.data);
    } catch (e) {
      setErr("Nepodarilo sa načítať agentov.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const filtered = useMemo(() => {
    if (!data?.agents) return [];
    let rows = data.agents;
    if (filter === "live") rows = rows.filter((a) => !a.coming_soon);
    if (filter === "soon") rows = rows.filter((a) => a.coming_soon);
    return [...rows].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  }, [data, filter, sortKey]);

  const t = data?.totals || {};

  return (
    <div className="p-4 space-y-5" data-testid="admin-agents-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          🤖 AI Agenti · Prehľad & štatistiky
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="bg-zinc-900 border border-zinc-700 text-xs px-2 py-1 text-white"
            data-testid="agents-days-select"
          >
            <option value="7">7 dní</option>
            <option value="30">30 dní</option>
            <option value="90">90 dní</option>
            <option value="365">365 dní</option>
          </select>
          <button
            onClick={fetchData}
            className="text-xs bg-amber-500 hover:bg-amber-400 text-black font-bold px-3 py-1"
            data-testid="agents-refresh-btn"
          >
            ↻ Obnoviť
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-900/40 border border-red-700 text-red-200 text-xs p-3">
          {err}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Agentov spolu" value={t.agents_count || 0} accent="text-white" testid="kpi-total" />
        <Kpi label="🟢 Live" value={t.live_count || 0} accent="text-emerald-400" testid="kpi-live" />
        <Kpi label="🟡 Coming soon" value={t.coming_soon_count || 0} accent="text-amber-400" testid="kpi-soon" />
        <Kpi label={`Behov za ${days} dní`} value={t.runs_30d_total || 0} accent="text-blue-400" testid="kpi-runs" />
      </div>

      {/* MRR estimate */}
      <div className="bg-gradient-to-r from-emerald-950/50 to-zinc-950 border border-emerald-500/30 p-3">
        <div className="text-[10px] tracking-widest uppercase font-black text-emerald-400">
          💰 Estimated MRR (z aktívnych workspaces × cena agenta)
        </div>
        <div className="text-2xl font-black text-white mt-1" data-testid="kpi-mrr">
          € {(t.est_mrr_eur || 0).toLocaleString("sk-SK")}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">
          Sum(primary_agent + additional_agents) × price · across all workspaces
        </div>
      </div>

      {/* Filter + Sort */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1">
          {[
            { id: "all", label: `Všetci (${t.agents_count || 0})` },
            { id: "live", label: `🟢 Live (${t.live_count || 0})` },
            { id: "soon", label: `🟡 Coming soon (${t.coming_soon_count || 0})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-xs px-3 py-1 ${
                filter === f.id
                  ? "bg-amber-500 text-black font-bold"
                  : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
              }`}
              data-testid={`agents-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-zinc-500">Zoradiť:</span>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-white"
            data-testid="agents-sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {loading && <p className="text-xs text-zinc-500">Načítavam…</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-xs text-zinc-500">Žiadni agenti v tomto filtri.</p>
      )}
      {!loading && filtered.length > 0 && (
        <div className="overflow-x-auto border border-zinc-800">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-400">
              <tr>
                <th className="text-left p-2">Agent</th>
                <th className="text-right p-2">Cena</th>
                <th className="text-center p-2">Stav</th>
                <th className="text-right p-2">Klienti</th>
                <th className="text-right p-2">Behov</th>
                <th className="text-right p-2">👍 %</th>
                <th className="text-right p-2">Fallback %</th>
                <th className="text-right p-2">Demand</th>
                <th className="text-right p-2">Latencia</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.slug}
                  className="border-t border-zinc-800 hover:bg-zinc-900/50"
                  data-testid={`agent-row-${a.slug}`}
                >
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{a.emoji}</span>
                      <div>
                        <div className="font-bold text-white">{a.name}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">{a.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-2 text-right text-amber-400 font-bold">€{a.price_eur_month}</td>
                  <td className="p-2 text-center">
                    {a.coming_soon ? (
                      <span className="text-[10px] bg-amber-900 text-amber-300 px-1.5 py-0.5">SOON</span>
                    ) : (
                      <span className="text-[10px] bg-emerald-900 text-emerald-300 px-1.5 py-0.5">LIVE</span>
                    )}
                  </td>
                  <td className="p-2 text-right text-white font-bold">{a.active_workspaces || 0}</td>
                  <td className="p-2 text-right text-blue-400 font-bold">{a.runs_30d || 0}</td>
                  <td className="p-2 text-right">
                    {a.feedback_total > 0
                      ? <span className="text-emerald-400 font-bold">{a.thumbs_up_pct}%</span>
                      : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="p-2 text-right">
                    {a.runs_30d > 0
                      ? <span className={a.fallback_pct > 20 ? "text-red-400 font-bold" : "text-zinc-400"}>{a.fallback_pct}%</span>
                      : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="p-2 text-right text-amber-300">{a.demand_votes || 0}</td>
                  <td className="p-2 text-right text-zinc-400">
                    {a.avg_latency_ms > 0 ? `${Math.round(a.avg_latency_ms)} ms` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[10px] text-zinc-600 pt-2 border-t border-zinc-800">
        Dáta z <code>/api/agents/overview</code> · joinuje AGENT_CATALOG + agent_executions ({days}d) + agent_waitlist + agent_os_workspaces
      </div>
    </div>
  );
}

const Kpi = ({ label, value, accent, testid }) => (
  <div className="bg-zinc-900 border border-zinc-800 p-3" data-testid={testid}>
    <div className="text-[10px] tracking-widest uppercase font-black text-zinc-500">
      {label}
    </div>
    <div className={`text-2xl font-black mt-1 ${accent}`}>{value}</div>
  </div>
);
